import { Center, Heading, HStack, Text, VStack } from '@chakra-ui/react'
import { faCalendar, faClock } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { max, min } from 'lodash-es'
import { DateTime } from 'luxon'

import type { SeriesNonParticipantSidebarData } from '@/common/services/seriesPage/types'
import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import { findScheduleAmongSessions } from '@/client/services/session'
import { Repeats, type TypicalStartTime } from '@/client/services/session/findScheduleAmongSessions'

import SeriesRegistrationButton from '@/components/Series/SeriesRegistration/SeriesRegistrationButton'
import SeriesReservationCancel from '@/components/Series/SeriesRegistration/SeriesReservationCancel'
import Card from '@/components/Worlds/UI/Card'

function TimeCard({
  typicalStartTime,
  isTimespanOneWeek,
}: {
  typicalStartTime: TypicalStartTime
  isTimespanOneWeek: boolean
}) {
  return (
    <HStack
      px="16px"
      py="6px"
      border={`2px solid ${chakraTokenToVar('border')}`}
      borderRadius="8px"
      alignItems="center"
      justifyContent="space-between"
    >
      <Text size="md" color="fg.muted">
        {isTimespanOneWeek
          ? typicalStartTime.sampleTime.toFormat('cccc, LLL d')
          : typicalStartTime.repeats === Repeats.Daily ||
              typicalStartTime.repeats === Repeats.Weekdays
            ? Repeats[typicalStartTime.repeats]
            : `${typicalStartTime.sampleTime.toFormat('cccc')}s`}
      </Text>
      <Text size="md" color="fg.muted" suppressHydrationWarning>
        {typicalStartTime.sampleTime.toFormat('h:mma')}
      </Text>
    </HStack>
  )
}

export default function SeriesNonParticipantSidebar({
  series,
}: {
  series: SeriesNonParticipantSidebarData
}) {
  const sessions = series.sessions.map(session => ({
    startDate: DateTime.fromISO(session.startDate),
    endDate: DateTime.fromISO(session.endDate),
    hasEnded: session.hasEnded,
  }))

  const upcomingSessions = sessions.filter(session => !session.hasEnded)

  const firstSession = sessions[0]
  const lastSession = sessions[sessions.length - 1]

  if (firstSession == null || lastSession == null) {
    return null
  }

  const seriesTimespan = lastSession.endDate.diff(firstSession.startDate)
  const isTimespanOneDay = seriesTimespan.as('days') <= 1
  const isTimespanOneWeek = seriesTimespan.as('weeks') <= 1

  const sessionDurations = sessions.map(session =>
    session.endDate.diff(session.startDate).as('minutes')
  )
  const minDuration = Math.floor(min(sessionDurations) ?? 0)
  const maxDuration = Math.floor(max(sessionDurations) ?? 0)

  const typicalStartTimes = findScheduleAmongSessions(sessions)

  const isUserSelfReserved = series.viewer.isSelfReserved

  return (
    <Card p={0}>
      <VStack py="24px" px="16px" gap="16px" alignItems="stretch">
        <VStack gap="2px" alignItems="stretch">
          <Heading size="xl">
            {firstSession.startDate.toFormat('LLL d')}
            {!isTimespanOneDay ? ` - ${lastSession.endDate.toFormat('LLL d')}` : null}
          </Heading>
          <HStack gap="8px">
            <HStack gap="4px">
              <Center width="14px">
                <FontAwesomeIcon
                  size="sm"
                  icon={faCalendar}
                  color={chakraTokenToVar('fg.subtle')}
                />
              </Center>
              <Text fontWeight="semibold" fontSize="sm">
                {pluralize(Math.ceil(seriesTimespan.as('weeks')), 'week')}
              </Text>
            </HStack>
            <HStack gap="4px">
              <FontAwesomeIcon size="sm" icon={faClock} color={chakraTokenToVar('fg.subtle')} />
              <Text fontWeight="semibold" fontSize="sm">
                {minDuration === maxDuration ? minDuration : `${minDuration} - ${maxDuration}`} mins
              </Text>
              <Text fontSize="sm">/ session</Text>
            </HStack>
          </HStack>
          {upcomingSessions.length > 0 ? (
            <Text fontWeight="semibold" color="fg.muted" fontSize="sm">
              Next session on {upcomingSessions[0].startDate.toFormat('LLLL d, y')}
            </Text>
          ) : null}
        </VStack>
        {typicalStartTimes.length > 0 ? (
          <VStack gap="8px" alignItems="stretch">
            <Text variant="eyebrow" color="fg.subtle" textAlign="left">
              SCHEDULE
            </Text>
            {typicalStartTimes.map(typicalStartTime => (
              <TimeCard
                key={typicalStartTime.sampleTime.toLocaleString()}
                typicalStartTime={typicalStartTime}
                isTimespanOneWeek={isTimespanOneWeek}
              />
            ))}
          </VStack>
        ) : null}
        <VStack spacing="8px" alignItems="stretch">
          <SeriesReservationCancel series={series} isUserSelfReserved={isUserSelfReserved} />
          <SeriesRegistrationButton series={series} isUserSelfReserved={isUserSelfReserved} />
        </VStack>
      </VStack>
    </Card>
  )
}
